import {
  TrainingRecord,
  TrainingCompletionStatus,
  TrainingTableSortColumn,
} from './types';
import { SortDirection } from '../../../Types/SortDirection';
import { ALL_FILTER_VALUE } from '../types/constants';

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const isOverdue = (
  dueDate: string,
  completionStatus: TrainingCompletionStatus
): boolean => {
  if (completionStatus === TrainingCompletionStatus.COMPLETED) {
    return false;
  }
  const due = new Date(dueDate);
  const now = new Date();
  return due < now;
};

export const getStatusColor = (
  status: TrainingCompletionStatus
): 'success' | 'warning' | 'neutral' | 'theme' => {
  switch (status) {
    case TrainingCompletionStatus.COMPLETED:
      return 'success';
    case TrainingCompletionStatus.IN_PROGRESS:
      return 'theme';
    case TrainingCompletionStatus.OVERDUE:
      return 'warning';
    case TrainingCompletionStatus.NOT_STARTED:
    default:
      return 'neutral';
  }
};

export const sortTrainings = (
  trainings: TrainingRecord[],
  sortColumn: TrainingTableSortColumn,
  sortDirection: SortDirection
): TrainingRecord[] => {
  if (!trainings || !Array.isArray(trainings)) {
    return [];
  }

  return [...trainings].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    switch (sortColumn) {
      case TrainingTableSortColumn.ASSOCIATE_NAME:
        aValue = a.associateName.toLowerCase();
        bValue = b.associateName.toLowerCase();
        break;
      case TrainingTableSortColumn.COURSE_NAME:
        aValue = a.courseName.toLowerCase();
        bValue = b.courseName.toLowerCase();
        break;
      case TrainingTableSortColumn.CCX_ATTRIBUTE:
        aValue = a.ccxAttributes[0]?.toLowerCase();
        bValue = b.ccxAttributes[0]?.toLowerCase();
        break;
      case TrainingTableSortColumn.COMPLETION_STATUS:
        // Sort by status priority: Overdue, In Progress, Not Started, Completed
        const statusPriority = {
          [TrainingCompletionStatus.OVERDUE]: 0,
          [TrainingCompletionStatus.IN_PROGRESS]: 1,
          [TrainingCompletionStatus.NOT_STARTED]: 2,
          [TrainingCompletionStatus.COMPLETED]: 3,
        };
        aValue = statusPriority[a.completionStatus];
        bValue = statusPriority[b.completionStatus];
        break;
      case TrainingTableSortColumn.DUE_DATE:
        aValue = new Date(a.dueDate).getTime();
        bValue = new Date(b.dueDate).getTime();
        break;
      case TrainingTableSortColumn.DURATION:
        aValue = a.duration;
        bValue = b.duration;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) {
      return sortDirection === SortDirection.ASCENDING ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortDirection === SortDirection.ASCENDING ? 1 : -1;
    }
    return 0;
  });
};

export const filterTrainings = (
  trainings: TrainingRecord[],
  ccxMetric: string,
  trainingStatus: string,
  searchTerm: string | undefined
): TrainingRecord[] => {
  if (!trainings || !Array.isArray(trainings)) {
    return [];
  }

  return trainings.filter(training => {
    // Search term filter (searches course name)
    if (
      searchTerm &&
      !training.courseName.toLowerCase().includes(searchTerm.toLowerCase())
    ) {
      return false;
    }

    // CCX metric filter
    if (
      ccxMetric &&
      ccxMetric !== ALL_FILTER_VALUE &&
      !training.ccxAttributes.includes(ccxMetric)
    ) {
      return false;
    }

    // Completion status filter
    if (
      trainingStatus &&
      trainingStatus !== ALL_FILTER_VALUE &&
      trainingStatus !== training.completionStatus
    ) {
      return false;
    }

    return true;
  });
};

export const getTrailingThreeMonthsDate = (): string => {
  const date = new Date();
  date.setMonth(date.getMonth() - 3);
  return date.toISOString().split('T')[0];
};

export const isWithinTrailingThreeMonths = (dateString: string): boolean => {
  const date = new Date(dateString);
  const threeMonthsAgo = new Date();
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
  return date >= threeMonthsAgo;
};

export const getUniqueValues = <T>(array: T[], key: keyof T): T[keyof T][] => {
  const uniqueValues = new Set(array.map(item => item[key]));
  return Array.from(uniqueValues);
};

export const getCcxMetricTranslation = (metric: string) => {
  const metricKey = metric.toLowerCase().replace(/\s+/g, '_');

  const translations = {
    courtesy: {
      id: 'cs_perfportal_ccx_metric_courtesy',
      defaultMessage: 'Courtesy',
    },
    empathy: {
      id: 'cs_perfportal_ccx_metric_empathy',
      defaultMessage: 'Empathy',
    },
    knowledgeable: {
      id: 'cs_perfportal_ccx_metric_knowledgeable',
      defaultMessage: 'Knowledgeable',
    },
    understandability: {
      id: 'cs_perfportal_ccx_metric_understandability',
      defaultMessage: 'Understandability',
    },
    helpfulness: {
      id: 'cs_perfportal_ccx_metric_helpfulness',
      defaultMessage: 'Helpfulness',
    },
    efficiency: {
      id: 'cs_perfportal_ccx_metric_efficiency',
      defaultMessage: 'Efficiency',
    },
  };

  return (
    translations[metricKey] || {
      id: 'cs_perfportal_ccx_no_attributes',
      defaultMessage: 'N/A',
    }
  );
};

export const getTrainingStatusTranslation = (
  status: TrainingCompletionStatus
) => {
  const translations = {
    [TrainingCompletionStatus.NOT_STARTED]: {
      id: 'cs_perfportal_training_status_not_started',
      defaultMessage: 'Not Started',
    },
    [TrainingCompletionStatus.IN_PROGRESS]: {
      id: 'cs_perfportal_training_status_in_progress',
      defaultMessage: 'In Progress',
    },
    [TrainingCompletionStatus.COMPLETED]: {
      id: 'cs_perfportal_training_status_completed',
      defaultMessage: 'Completed',
    },
    [TrainingCompletionStatus.OVERDUE]: {
      id: 'cs_perfportal_training_status_overdue',
      defaultMessage: 'Overdue',
    },
  };

  return (
    translations[status] || {
      id: 'cs_perfportal_training_no_status',
      defaultMessage: 'N/A',
    }
  );
};
